import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Calendar,
    ChefHat,
    DollarSign,
    ArrowRight,
    Loader2,
    Crown,
    Clock,
    History,
    ChevronLeft,
    Sparkles
} from 'lucide-react';

export const PackHistoryPage = () => {
    const { user, isPremium } = useAuth();
    const navigate = useNavigate();
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        if (!isPremium) {
            // Redirect non-premium users
            return;
        }
        loadPacks();
    }, [isPremium]);

    const loadPacks = async (skip = 0) => {
        try {
            if (skip === 0) setLoading(true);
            else setLoadingMore(true);
            
            const response = await api.getWeeklyPacksHistory(skip, 12);
            const data = response.data;
            
            if (skip === 0) {
                setPacks(data.packs);
            } else {
                setPacks(prev => [...prev, ...data.packs]);
            }
            setHasMore(data.has_more);
            setTotal(data.total);
        } catch (error) {
            console.error('Error loading pack history:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadMore = () => {
        loadPacks(packs.length);
    };

    // Show premium upgrade prompt for non-premium users
    if (!isPremium) {
        return (
            <div className="min-h-screen py-12">
                <div className="max-w-2xl mx-auto px-4 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <Crown className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="font-heading text-3xl font-bold mb-4">
                        Historique des Packs
                    </h1>
                    <p className="text-muted-foreground text-lg mb-8">
                        Accédez à tous les packs hebdomadaires précédents avec un abonnement Premium.
                    </p>
                    <Button
                        onClick={() => navigate('/subscription')}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-full"
                    >
                        <Crown className="w-5 h-5 mr-2" />
                        Devenir Premium
                    </Button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="font-medium text-muted-foreground">Chargement de l'historique...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 md:py-12">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/weekly-pack')}
                        className="mb-4"
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Pack actuel
                    </Button>
                    
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                            <History className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                                Historique des Packs
                            </h1>
                            <p className="text-muted-foreground">
                                {total} pack{total > 1 ? 's' : ''} disponible{total > 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Packs Grid */}
                {packs.length === 0 ? (
                    <div className="text-center py-12">
                        <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-lg text-muted-foreground">Aucun pack dans l'historique</p>
                    </div>
                ) : (
                    <>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {packs.map((pack, index) => (
                                <PackCard 
                                    key={pack.id} 
                                    pack={pack} 
                                    isLatest={index === 0}
                                />
                            ))}
                        </div>

                        {/* Load More Button */}
                        {hasMore && (
                            <div className="text-center mt-8">
                                <Button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    variant="outline"
                                    className="px-8"
                                >
                                    {loadingMore ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Chargement...
                                        </>
                                    ) : (
                                        <>
                                            Voir plus de packs
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const PackCard = ({ pack, isLatest }) => {
    const navigate = useNavigate();
    
    // Format date
    const formatDate = (dateStr) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('fr-CA', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
            });
        } catch {
            return '';
        }
    };

    // Color based on pack number
    const colors = [
        'from-green-500 to-emerald-600',
        'from-blue-500 to-cyan-600',
        'from-purple-500 to-pink-600',
        'from-orange-500 to-red-600',
        'from-teal-500 to-green-600'
    ];
    const colorClass = colors[(pack.pack_number || 1) % colors.length];

    return (
        <Card 
            className="group cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden border-2 hover:border-primary/30"
            onClick={() => navigate(`/weekly-pack/${pack.id}`)}
            data-testid={`pack-card-${pack.id}`}
        >
            {/* Header with gradient */}
            <div className={`bg-gradient-to-r ${colorClass} p-4 text-white relative`}>
                {isLatest && (
                    <Badge className="absolute top-2 right-2 bg-white/20 text-white border-white/30">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Dernier
                    </Badge>
                )}
                <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-white/20 text-white border-0 font-bold">
                        Pack #{pack.pack_number || pack.week_number}
                    </Badge>
                </div>
                <h3 className="font-heading font-bold text-lg line-clamp-1">
                    {pack.title}
                </h3>
            </div>

            <CardContent className="p-4">
                {/* Date */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(pack.created_at)}</span>
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                    {pack.description}
                </p>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-primary">
                        <ChefHat className="w-4 h-4" />
                        <span className="font-medium">{pack.soupers_count} soupers</span>
                    </div>
                    {pack.total_cost > 0 && (
                        <div className="flex items-center gap-1 text-green-600">
                            <DollarSign className="w-4 h-4" />
                            <span className="font-medium">{pack.total_cost.toFixed(2)}$</span>
                        </div>
                    )}
                </div>

                {/* Soupers preview */}
                {pack.soupers_preview && pack.soupers_preview.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                        <div className="flex -space-x-2">
                            {pack.soupers_preview.map((souper, i) => (
                                <div 
                                    key={i}
                                    className="w-10 h-10 rounded-full bg-muted border-2 border-background overflow-hidden"
                                    title={souper.title}
                                >
                                    {souper.image_url ? (
                                        <img 
                                            src={souper.image_url} 
                                            alt={souper.title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-primary/10">
                                            <ChefHat className="w-5 h-5 text-primary" />
                                        </div>
                                    )}
                                </div>
                            ))}
                            {pack.soupers_count > 3 && (
                                <div className="w-10 h-10 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs font-medium">
                                    +{pack.soupers_count - 3}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Arrow indicator */}
                <div className="flex justify-end mt-4">
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </div>
            </CardContent>
        </Card>
    );
};

export default PackHistoryPage;
